import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Skeleton } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import { deleteServiceById } from "../../../../common/network/Api";
import MoreIcon from "../../svg/MoreIcon";
import type { ServiceDetails } from "./types";
import useTheme from "../../../../common/hooks/useTheme";
import MoreOptionsPopover, { MenuOption } from "../common/MoreOptionsPopover";
import RelevantLogicsSection from "./RelevantLogicsSection";
import { serviceLogicBuilderActions } from "../../store/service-logic-builder-slice";
import ServiceInformationDetails from "../common/ServiceInformationDetails";
import { enReportServiceType } from "./helper";

const ServiceDetailsView = ({
  service,
  theme,
  loadData,
  savedServices,
  setSelectedService,
  setEditingService,
  setIsEditMode,
  setOpenModal,
}: {
  service: ServiceDetails;
  theme: Theme;
  loadData: () => Promise<void>;
  savedServices: ServiceDetails[];
  setSelectedService: (service: ServiceDetails | null) => void;
  setEditingService: (service: ServiceDetails) => void;
  setIsEditMode: (isEdit: boolean) => void;
  setOpenModal: (open: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const detailsRef = React.useRef<HTMLDivElement>(null);
  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetSnackBarActions,
    ASSetTypeSnackBar,
  } = useAppStore();
  // Get image URL for current service
  const imageUrl = service.imageURL || "/placeholder-image.png";


  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleEdit = () => {
    // Pass the service directly - the modal will handle cloning
    setEditingService(service);
    setIsEditMode(true);
    setOpenModal(true);
  };

  const handleDelete = () => {
    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete service",
      message: `Are you sure you want to delete "${service.name}"? This action cannot be undone.`,
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        dispatch(serviceLogicBuilderActions.setLoading(true));
        try {
          if (!service.id) {
            throw new Error("Service ID is missing");
          }
          await deleteServiceById(service.id);

          // Find the next service to select
          const currentIndex = savedServices.findIndex(
            (s) => s.id === service.id
          );
          let nextService: ServiceDetails | null = null;

          if (savedServices.length > 1) {
            // If there's a next service, select it
            if (currentIndex < savedServices.length - 1) {
              nextService = savedServices[currentIndex + 1];
            } else if (currentIndex > 0) {
              // If this is the last service, select the previous one
              nextService = savedServices[currentIndex - 1];
            }
          }

          await loadData(); // Reload the data to update the list

          // Set the next service as selected, or null if no services left
          setSelectedService(nextService);

          // Show success snackbar
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: `Service "${service.name}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");
        } catch (error) {
          console.error("Error deleting service:", error);
          // Show error snackbar
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete service. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        } finally {
          dispatch(serviceLogicBuilderActions.setLoading(false));
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  const menuOptions: MenuOption[] = [
    { label: "Edit", onClick: handleEdit },
    { label: "Delete", onClick: handleDelete },
  ];

  return (
    <div ref={detailsRef} style={styles.serviceDetailsWrapper(theme)}>
      <div style={styles.serviceDetailsHeader(theme)}>
        <div
          style={{
            position: "relative",
            width: "256px",
            height: "122px",
            overflow: "hidden",
            borderRadius: "8px",
          }}
        >
          {imageLoading && (
            <Skeleton
              variant="rectangular"
              width={"100%"}
              height={"100%"}
              style={{
                borderRadius: "8px",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
          )}
          {imageError ? (
            <div style={styles.imagePlaceholder(theme)}>
              <div style={styles.imagePlaceholderText(theme)}>No image</div>
            </div>
          ) : (
            <img
              key={service.id}
              src={imageUrl}
              alt={service.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                width: "100%",
                opacity: imageLoading ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
              }}
            />
          )}
        </div>
        <div style={styles.detailTitleContainer}>
          <div style={styles.serviceDetailsTitle(theme)}>{service.name}</div>

          <div style={styles.serviceDetailsType(theme)}>
            {enReportServiceType[service.serviceType] || ""}
          </div>
          <div style={styles.serviceDetailsDescription(theme)}>
            {service.description || "No description provided"}
          </div>
        </div>
        <MoreOptionsPopover
          options={menuOptions}
          icon={<MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />}
        />
      </div>
      <div style={styles.serviceDetailsContent(theme)}>
        <div style={styles.serviceDetailsContentWrapper(theme)}>
          <ServiceInformationDetails service={service} />
        </div>
      </div>
      {/* Relevant logic(s) section */}
      <RelevantLogicsSection service={service} loadData={loadData} />
    </div>
  );
};

const styles = {
  serviceDetailsWrapper: (theme: any) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "space-between",
    gap: "24px",
    // flex: 1,
  }),

  serviceDetailsHeader: (theme: any) => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "24px",
    paddingBottom: "24px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),

  serviceDetailsContent: (theme: any) => ({
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "flex-start",
    gap: "16px",
    paddingBottom: "24px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  serviceDetailsContentWrapper: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    height: "100%",
  }),
  detailTitleContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "flex-start",
    gap: "4px",
    paddingTop: "4px",
  },
  serviceDetailsTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  serviceDetailsType: (theme: any) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  serviceDetailsDescription: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    paddingTop: "12px",
  }),
  imagePlaceholder: (theme: any) => ({
    width: "120px",
    height: "120px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_WEAK,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  imagePlaceholderText: (theme: any) => ({
    fontSize: "10px",
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as "center",
  }),
};

export default ServiceDetailsView;
